import React from "react";
import moment from "moment";
import UIcon from "@/assets/uicons/UIcon";
import { UserTooltip } from "@/components/UI";

interface Avatar {
  name: string;
  avatarUrl?: string;
  role?: string;
  email?: string;
  localTime?: string;
  collaboratingProjects?: number;
}

interface TaskListRowProps {
  index: number;
  title: string;
  assignees?: Avatar[];
  dateRange?: { start: string; end: string };
  status?: "pendiente" | "en progreso" | "completado";
  commentsCount?: number;
  attachmentsCount?: number;
  onEdit?: () => void;
  onMenu?: () => void;
  onTitleClick?: () => void;
  styles?: Record<string, string>;
}

const TaskListRow: React.FC<TaskListRowProps> = ({
  index,
  title,
  assignees = [],
  dateRange,
  status = "pendiente",
  commentsCount = 0,
  attachmentsCount = 0,
  onEdit,
  onMenu,
  onTitleClick,
  styles = {},
}) => {
  // Determinar clase de badge
  const badgeClass =
    status === "completado"
      ? `${styles["task-table__badge"] || "task-table__badge"} ${styles["task-table__badge--completed"] || "task-table__badge--completed"}`
      : `${styles["task-table__badge"] || "task-table__badge"} ${styles["task-table__badge--pending"] || "task-table__badge--pending"}`;

  return (
    <>
      {/* Número */}
      <td className={`${styles["task-table__cell"] || "task-table__cell"} ${styles["task-table__cell--number"] || "task-table__cell--number"}`}>{index + 1}</td>
      {/* Nombre + iconos */}
      <td className={`${styles["task-table__cell"] || "task-table__cell"} ${styles["task-table__cell--title"] || "task-table__cell--title"}`}>
        <div className={styles["task-table__title"] || "task-table__title"}>
          <UIcon name={status === "completado" ? "check-circle" : "circle"} size={16} />
          <span 
            style={{ flex: 1, cursor: 'pointer' }}
            onClick={onTitleClick}
            className={styles["task-table__title-text"] || "task-table__title-text"}
          >
            {title}
          </span>
          {/* Iconos en la misma línea */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: '0.5rem' }}>
            {commentsCount > 0 && (
              <span className={styles["task-table__icon"] || "task-table__icon"}>
                <UIcon name="comment" size={14} /> {commentsCount}
              </span>
            )}
            {attachmentsCount > 0 && (
              <span className={styles["task-table__icon"] || "task-table__icon"}>
                <UIcon name="attachment" size={14} /> {attachmentsCount}
              </span>
            )}
          </div>
        </div>
      </td>
      {/* Responsable */}
      <td className={`${styles["task-table__cell"] || "task-table__cell"} ${styles["task-table__cell--assignee"] || "task-table__cell--assignee"}`}>
        {assignees.length > 0 ? (
          <UserTooltip
            user={{
              name: assignees[0].name,
              avatarUrl: assignees[0].avatarUrl,
              role: assignees[0].role,
              email: assignees[0].email,
              localTime: assignees[0].localTime,
              collaboratingProjects: assignees[0].collaboratingProjects
            }}
            placement="top"
            onAssignTask={() => console.log(`Asignar tarea a ${assignees[0].name}`)}
            onViewProfile={() => console.log(`Ver perfil de ${assignees[0].name}`)}
          >
            <div className={styles["task-table__assignee"] || "task-table__assignee"}>
              <span className={styles["task-table__avatar"] || "task-table__avatar"}>
                {assignees[0].avatarUrl ? (
                  <img src={assignees[0].avatarUrl} alt={assignees[0].name} />
                ) : (
                  assignees[0].name
                    .split(" ")
                    .slice(0, 2)
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                )}
              </span>
              <span className={styles["task-table__assignee-name"] || "task-table__assignee-name"}>
                {assignees[0].name}
              </span>
            </div>
          </UserTooltip>
        ) : (
          <div className={styles["task-table__assignee"] || "task-table__assignee"}>
            <span className={`${styles["task-table__avatar"] || "task-table__avatar"} ${styles["task-table__avatar--empty"] || "task-table__avatar--empty"}`}>
              <UIcon name="user" size={14} />
            </span>
            <span className={styles["task-table__assignee-name"] || "task-table__assignee-name"}>
              Sin asignar
            </span>
          </div>
        )}
      </td>
      {/* Fecha */}
      <td className={`${styles["task-table__cell"] || "task-table__cell"} ${styles["task-table__cell--date"] || "task-table__cell--date"}`}>
        {dateRange ? (
          <span>
            {moment(dateRange.start).format("MMM D")} – {moment(dateRange.end).format("MMM D")}
          </span>
        ) : (
          <span className={styles["task-table__date--empty"] || "task-table__date--empty"}>
            <UIcon name="calendar" size={14} />
          </span>
        )}
      </td>
      {/* Estado */}
      <td className={`${styles["task-table__cell"] || "task-table__cell"} ${styles["task-table__cell--status"] || "task-table__cell--status"}`}>
        <span className={badgeClass}>
          {status === "completado"
            ? "Completado"
            : status === "en progreso"
            ? "En progreso"
            : "Pendiente"}
        </span>
      </td>
      {/* Acciones */}
      <td className={`${styles["task-table__cell"] || "task-table__cell"} ${styles["task-table__cell--actions"] || "task-table__cell--actions"}`}>
        <button onClick={onEdit} aria-label="Editar tarea" className={styles["task-table__action-btn"] || "task-table__action-btn"}>
          <UIcon name="edit" size={14} />
        </button>
        <button onClick={onMenu} aria-label="Más opciones" className={styles["task-table__action-btn"] || "task-table__action-btn"}>
          <UIcon name="more" size={14} />
        </button>
      </td>
    </>
  );
};

export default TaskListRow; 